import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { TablePaymentRow } from "../types/invoice-table.types";

export interface PaymentNoteDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (note: string) => Promise<void>;
	actionType: 'cancel' | 'rebook';
	payment: TablePaymentRow;
	loading?: boolean;
}

export const PaymentNoteDialog: React.FC<PaymentNoteDialogProps> = ({
	isOpen,
	onClose,
	onConfirm,
	actionType,
	payment,
	loading = false,
}) => {
	const [note, setNote] = useState("");

	useEffect(() => {
		if (isOpen) {
			// Pre-fill with current allocation note for rebook, or empty for cancel
			if (actionType === 'rebook') {
				setNote(payment.allocation_note || "");
			} else {
				setNote("");
			}
		}
	}, [isOpen, actionType, payment]);

	const handleConfirm = async () => {
		await onConfirm(note.trim());
	};

	const handleClose = () => {
		setNote("");
		onClose();
	};

	const getTitle = () => {
		return actionType === 'cancel' ? 'Zahlung stornieren' : 'Zu Akonto umbuchen';
	};

	const getDescription = () => {
		return actionType === 'cancel'
			? 'Fügen Sie eine Notiz zur Stornierung hinzu.'
			: 'Fügen Sie eine Notiz zur Umbuchung hinzu.';
	};

	const getLabel = () => {
		return actionType === 'cancel' ? 'Notiz für Stornierung' : 'Notiz für Umbuchung';
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[500px]" aria-label="payment-note-dialog" data-testid="payment-note-dialog">
				<DialogHeader>
					<DialogTitle>{getTitle()}</DialogTitle>
					<DialogDescription>
						{getDescription()}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					{/* Existing payment notes (read-only) */}
					{(payment.payment_note_1 || payment.payment_note_2 || payment.allocation_note) && (
						<div className="space-y-2">
							<Label className="text-sm font-medium text-gray-700">Bestehende Notizen</Label>
							<div className="space-y-1 text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
								{payment.allocation_note && (
									<div>
										<span className="font-medium">Zuordnungsnotiz:</span> {payment.allocation_note}
									</div>
								)}
								{payment.payment_note_1 && (
									<div>
										<span className="font-medium">Zahlungsnotiz 1:</span> {payment.payment_note_1}
									</div>
								)}
								{payment.payment_note_2 && (
									<div>
										<span className="font-medium">Stornierungsgrund:</span> {payment.payment_note_2}
									</div>
								)}
							</div>
						</div>
					)}

					{/* Note input */}
					<div className="space-y-2">
						<Label htmlFor="note" className="text-sm font-medium">
							{getLabel()}
						</Label>
						<Textarea
							id="note"
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="Notiz eingeben..."
							rows={4}
							className="resize-none"
							disabled={loading}
						/>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={handleClose}
						disabled={loading}
					>
						Abbrechen
					</Button>
					<Button
						onClick={handleConfirm}
						disabled={loading || !note.trim()}
					>
						{loading ? "Wird verarbeitet..." : "Bestätigen"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
